/**
 * View Icons Utility
 * 
 * Provides icon resolution for views in ViewSelector and CoreSettings.
 * Default views use fixed icons; custom views can choose from a curated set.
 */

import {
  Calendar,
  Star,
  ListTodo,
  Clock,
  Sparkles,
  Tag,
  CheckSquare,
  Target,
  Briefcase,
  Home,
  Heart,
  Bookmark,
  Flag,
  Folder,
  FileText,
  Mail,
  Phone,
  MessageSquare,
  Bell,
  Settings,
  User,
  Users,
  Zap,
  Coffee,
  Sun,
  Moon,
  Cloud,
  Lightbulb,
  Rocket,
  Trophy,
  Gift,
  ShoppingCart,
  CreditCard,
  DollarSign,
  TrendingUp,
  BarChart,
  PieChart,
  Map,
  Compass,
  Globe,
  Music,
  Camera,
  Film,
  Book,
  GraduationCap,
  Dumbbell,
  Utensils,
  Car,
  Plane,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import type { View } from '../types';

/**
 * Fixed icons for default views (by slug)
 */
export const DEFAULT_VIEW_ICONS: Record<string, LucideIcon> = {
  today: Calendar,
  'this-week': Clock,
  'high-priority': Star,
  all: ListTodo,
  completed: CheckSquare,
};

/**
 * Curated list of 50 icon names for the icon picker
 */
export const AVAILABLE_ICON_NAMES = [
  // Row 1: Time & Calendar
  'Calendar', 'Clock', 'Sun', 'Moon', 'Bell',
  // Row 2: Priority & Status
  'Star', 'Flag', 'Target', 'Zap', 'Trophy',
  // Row 3: Tasks & Lists
  'ListTodo', 'CheckSquare', 'Bookmark', 'Tag', 'FileText',
  // Row 4: Communication
  'Mail', 'MessageSquare', 'Phone', 'Users', 'User',
  // Row 5: Work & Business
  'Briefcase', 'Folder', 'TrendingUp', 'BarChart', 'PieChart',
  // Row 6: Home & Personal
  'Home', 'Heart', 'Coffee', 'Utensils', 'Dumbbell',
  // Row 7: Finance
  'DollarSign', 'CreditCard', 'ShoppingCart', 'Gift', 'Wallet',
  // Row 8: Ideas & Learning
  'Lightbulb', 'Sparkles', 'Rocket', 'GraduationCap', 'Book',
  // Row 9: Travel & Places
  'Car', 'Plane', 'Map', 'Compass', 'Globe',
  // Row 10: Media & Creative
  'Camera', 'Film', 'Music', 'Cloud', 'Settings',
] as const;

/**
 * Map of icon name strings to Lucide components
 */
export const ICON_NAME_MAP: Record<string, LucideIcon> = {
  Calendar,
  Star,
  ListTodo,
  Clock,
  Sparkles,
  Tag,
  CheckSquare,
  Target,
  Briefcase,
  Home,
  Heart,
  Bookmark,
  Flag,
  Folder,
  FileText,
  Mail,
  Phone,
  MessageSquare,
  Bell,
  Settings,
  User,
  Users,
  Zap,
  Coffee,
  Sun,
  Moon,
  Cloud,
  Lightbulb,
  Rocket,
  Trophy,
  Gift,
  ShoppingCart,
  CreditCard,
  DollarSign,
  TrendingUp,
  BarChart,
  PieChart,
  Map,
  Compass,
  Globe,
  Music,
  Camera,
  Film,
  Book,
  GraduationCap,
  Dumbbell,
  Utensils,
  Car,
  Plane,
  Wallet,
};

/**
 * Get the Lucide icon component for a view.
 * - Default views: use fixed icon from DEFAULT_VIEW_ICONS
 * - Custom views: use filterConfig.icon if set, otherwise ListTodo
 */
export function getViewIconComponent(view: View): LucideIcon {
  // Default views have fixed icons
  if (view.isDefault && DEFAULT_VIEW_ICONS[view.slug]) {
    return DEFAULT_VIEW_ICONS[view.slug];
  }

  // Custom views: use icon from filterConfig if valid
  const iconName = view.filterConfig?.icon;
  if (iconName && ICON_NAME_MAP[iconName]) {
    return ICON_NAME_MAP[iconName];
  }

  // Fallback: check if slug matches a default icon (for edge cases)
  if (DEFAULT_VIEW_ICONS[view.slug]) {
    return DEFAULT_VIEW_ICONS[view.slug];
  }

  // Final fallback
  return ListTodo;
}

/**
 * Get icon component by name string (for icon picker preview)
 */
export function getIconByName(name: string): LucideIcon {
  return ICON_NAME_MAP[name] || ListTodo;
}
